import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';

interface UnifiedLoadingContextType {
  // Global loading (business data, auth)
  isGlobalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;

  // Tab-specific loading
  tabLoadingStates: Record<string, boolean>;
  setTabLoading: (tabId: string, loading: boolean) => void;

  // Derived states
  isAnyTabLoading: boolean;

  // Configuration
  loadingTransitionDelay: number;
}

const UnifiedLoadingContext = createContext<UnifiedLoadingContextType | undefined>(undefined);

interface UnifiedLoadingProviderProps {
  children: ReactNode;
  /** Minimum loading duration in ms to prevent micro-flickers (default: 150) */
  transitionDelay?: number;
}

export function UnifiedLoadingProvider({
  children,
  transitionDelay = 150
}: UnifiedLoadingProviderProps) {
  // Global loading state with debounce
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const globalLoadingTimeout = useRef<NodeJS.Timeout | null>(null);
  const globalLoadingStartTime = useRef<number | null>(null);

  // Tab loading states
  const [tabLoadingStates, setTabLoadingStates] = useState<Record<string, boolean>>({});

  // Debounced global loading setter
  const setGlobalLoading = useCallback((loading: boolean) => {
    if (globalLoadingTimeout.current) {
      clearTimeout(globalLoadingTimeout.current);
      globalLoadingTimeout.current = null;
    }

    if (loading) {
      // Start loading immediately, record start time
      globalLoadingStartTime.current = Date.now();
      setIsGlobalLoading(true);
    } else {
      // Ensure minimum loading duration to prevent flicker
      const elapsedTime = globalLoadingStartTime.current
        ? Date.now() - globalLoadingStartTime.current
        : transitionDelay;

      const remainingDelay = Math.max(0, transitionDelay - elapsedTime);

      if (remainingDelay > 0) {
        globalLoadingTimeout.current = setTimeout(() => {
          setIsGlobalLoading(false);
          globalLoadingStartTime.current = null;
        }, remainingDelay);
      } else {
        setIsGlobalLoading(false);
        globalLoadingStartTime.current = null;
      }
    }
  }, [transitionDelay]);

  // Tab loading setter with debounce per tab
  const tabLoadingTimeouts = useRef<Record<string, NodeJS.Timeout>>({});
  const tabLoadingStartTimes = useRef<Record<string, number>>({});

  const setTabLoading = useCallback((tabId: string, loading: boolean) => {
    // Clear any pending timeout for this tab
    if (tabLoadingTimeouts.current[tabId]) {
      clearTimeout(tabLoadingTimeouts.current[tabId]);
      delete tabLoadingTimeouts.current[tabId];
    }

    if (loading) {
      // Start loading immediately, record start time
      tabLoadingStartTimes.current[tabId] = Date.now();
      setTabLoadingStates(prev => ({ ...prev, [tabId]: true }));
    } else {
      // Ensure minimum loading duration
      const elapsedTime = tabLoadingStartTimes.current[tabId]
        ? Date.now() - tabLoadingStartTimes.current[tabId]
        : transitionDelay;

      const remainingDelay = Math.max(0, transitionDelay - elapsedTime);

      if (remainingDelay > 0) {
        tabLoadingTimeouts.current[tabId] = setTimeout(() => {
          setTabLoadingStates(prev => ({ ...prev, [tabId]: false }));
          delete tabLoadingStartTimes.current[tabId];
        }, remainingDelay);
      } else {
        setTabLoadingStates(prev => ({ ...prev, [tabId]: false }));
        delete tabLoadingStartTimes.current[tabId];
      }
    }
  }, [transitionDelay]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (globalLoadingTimeout.current) {
        clearTimeout(globalLoadingTimeout.current);
      }
      Object.values(tabLoadingTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  // Derived state
  const isAnyTabLoading = Object.values(tabLoadingStates).some(Boolean);

  const value: UnifiedLoadingContextType = {
    isGlobalLoading,
    setGlobalLoading,
    tabLoadingStates,
    setTabLoading,
    isAnyTabLoading,
    loadingTransitionDelay: transitionDelay,
  };

  return (
    <UnifiedLoadingContext.Provider value={value}>
      {children}
    </UnifiedLoadingContext.Provider>
  );
}

export function useUnifiedLoadingContext() {
  const context = useContext(UnifiedLoadingContext);
  if (context === undefined) {
    throw new Error('useUnifiedLoadingContext must be used within a UnifiedLoadingProvider');
  }
  return context;
}

// Optional: Hook that doesn't throw if used outside provider (returns defaults)
export function useUnifiedLoadingContextSafe() {
  const context = useContext(UnifiedLoadingContext);
  if (context === undefined) {
    // Return safe defaults for use outside provider
    return {
      isGlobalLoading: false,
      setGlobalLoading: () => {},
      tabLoadingStates: {},
      setTabLoading: () => {},
      isAnyTabLoading: false,
      loadingTransitionDelay: 150,
    };
  }
  return context;
}
